import { useEffect, useState } from "react";
import { ListingCard } from "@/components/ListingCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { fetchSimilarListings } from "@/lib/similarListings";

interface Props {
  listing: any;
}

export function SimilarListings({ listing }: Props) {
  const isMobile = useIsMobile();
  const desiredCount = isMobile ? 4 : 8;
  const [items, setItems] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!listing?.id) return;
    let cancelled = false;
    setLoading(true);
    setItems(null);
    setHidden(false);

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        setHidden(true);
        setLoading(false);
      }
    }, 3000);

    fetchSimilarListings(listing, desiredCount)
      .then((res) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        if (!res || res.length === 0) {
          setHidden(true);
        } else {
          setItems(res);
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setHidden(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [listing?.id, desiredCount]);

  if (hidden) return null;
  if (!loading && (!items || items.length === 0)) return null;

  const gridCols = isMobile ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  return (
    <section className="w-full max-w-full px-4 sm:px-6 lg:container lg:mx-auto py-8">
      <div className="mb-4">
        <h2 className="font-display text-xl font-bold text-heading md:text-2xl">
          Liknande annonser
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Andra annonser som liknar det du tittar på just nu.
        </p>
      </div>

      <div className={`grid gap-3 sm:gap-5 ${gridCols} items-stretch`}>
        {loading
          ? Array.from({ length: desiredCount }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-lg border border-border bg-card overflow-hidden"
              >
                <div className="bg-muted" style={{ aspectRatio: isMobile ? "1/1" : "4/3" }} />
                <div className="p-3 space-y-2">
                  <div className="h-3 w-1/2 rounded bg-muted" />
                  <div className="h-3 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-1/3 rounded bg-muted" />
                </div>
              </div>
            ))
          : items!.map((l) => (
              <div key={l.id} className="h-full [&>a]:h-full [&>a]:flex [&>a]:flex-col">
                <ListingCard
                  listing={{
                    id: l.id,
                    sellerName: "",
                    category: l.category as any,
                    species: l.species as any,
                    title: l.title,
                    description: l.description ?? "",
                    price: Number(l.price),
                    priceUnit: l.price_unit ?? "",
                    location: l.location ?? "",
                    delivery: l.delivery as any,
                    images: l.images ?? [],
                    status: l.status as any,
                  }}
                />
              </div>
            ))}
      </div>
    </section>
  );
}
